import { motion } from "motion/react";
import SEOHead from "@/components/SEOHead";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const CHECK = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="7" fill="rgba(200,144,10,0.15)" />
    <path d="M4 7l2 2 4-4" stroke="#C8900A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface Plan {
  name: string;
  tagline: string;
  description: string;
  price: string;
  priceNote: string;
  cta: string;
  ctaHref: string;
  featured?: boolean;
  features: string[];
  note?: string;
}

const plans: Plan[] = [
  {
    name: "Evaluation",
    tagline: "Prove the value before you commit.",
    description:
      "A time-limited pilot with one workflow and capped volume. Enough to show what the system does with your actual documents.",
    price: "Pilot",
    priceNote: "No cost to start",
    cta: "Start a pilot",
    ctaHref: "/contact",
    features: [
      "1 active workflow",
      "Up to 500 documents processed",
      "Core connectors (Drive, file stores, PDF)",
      "Structured extraction and tracking",
      "Shared Slack channel with our team",
    ],
    note: "Conversion to Team at end of pilot period.",
  },
  {
    name: "Team",
    tagline: "One department. Running in production.",
    description:
      "An annual contract for a single team or use case. Fixed number of workflows with defined volume limits.",
    price: "From $12k",
    priceNote: "per year, billed annually",
    cta: "Talk to sales",
    ctaHref: "/contact",
    featured: true,
    features: [
      "Up to 5 active workflows",
      "Up to 5,000 documents / month",
      "All core connectors",
      "Governance basics and document lineage",
      "Change detection and alerting",
      "Dedicated onboarding session",
      "Priority support with SLA",
    ],
  },
  {
    name: "Platform",
    tagline: "Multi-team. High volume. Full governance.",
    description:
      "A larger annual contract for organisations running Lumient across multiple departments with advanced compliance and support needs.",
    price: "Custom",
    priceNote: "based on workflows and volume",
    cta: "Contact us",
    ctaHref: "/contact",
    features: [
      "Unlimited active workflows",
      "High-volume document processing",
      "Advanced governance and full lineage",
      "Role-scoped access and permissioning",
      "Custom integrations and connectors",
      "Priority support and dedicated CSM",
      "SSO and enterprise security controls",
    ],
  },
];

const PlanCard = ({ plan, index }: { plan: Plan; index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay: index * 0.1, ease: EASE }}
    className={`relative flex flex-col rounded-2xl border p-8 lg:p-10 ${
      plan.featured
        ? "border-[rgba(200,144,10,0.5)] bg-[#111008]"
        : "border-white/10 bg-[#0F0F0F]"
    }`}
    style={
      plan.featured
        ? { boxShadow: "0 -24px 200px 0 rgba(200,144,10,0.12), 0 0 0 1px rgba(200,144,10,0.2) inset" }
        : undefined
    }
  >
    {plan.featured && (
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <span className="inline-block px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase bg-[#C8900A] text-[#0B0B0B]">
          Most common
        </span>
      </div>
    )}

    <div className="mb-8">
      <h3 className="font-serif font-semibold text-xl text-white mb-1 tracking-tight">{plan.name}</h3>
      <p className="text-xs font-medium text-[oklch(var(--primary-bright-readable))] mb-4">{plan.tagline}</p>
      <p className="text-sm text-surface-inverse-muted leading-relaxed font-light">{plan.description}</p>
    </div>

    <div className="mb-8 pb-8 border-b border-white/10">
      <div className="flex items-baseline gap-2">
        <span className="font-serif font-semibold text-4xl text-white tracking-tight">{plan.price}</span>
      </div>
      <span className="text-xs text-surface-inverse-muted mt-1 block">{plan.priceNote}</span>
    </div>

    <ul className="flex-1 space-y-3 mb-8">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <span className="mt-0.5 flex-shrink-0">{CHECK}</span>
          <span className="text-sm text-surface-inverse-muted leading-snug font-light">{f}</span>
        </li>
      ))}
    </ul>

    {plan.note && (
      <p className="text-xs text-surface-inverse-muted leading-relaxed mb-6 italic">{plan.note}</p>
    )}

    <a
      href={plan.ctaHref}
      className={`block text-center py-3 px-6 rounded-full text-sm font-semibold transition-all duration-200 ${
        plan.featured
          ? "bg-[oklch(var(--primary))] text-white hover:bg-[oklch(0.52_0.12_65)]"
          : "border border-white/15 text-white/70 hover:border-white/30 hover:text-white"
      }`}
    >
      {plan.cta}
    </a>
  </motion.div>
);

const Pricing = () => (
  <div className="min-h-screen bg-[#0B0B0B] text-foreground flex flex-col">
    <SEOHead
      title="Pricing — Lumient"
      description="Lumient starts with a free pilot. Production contracts begin in the low five figures annually, scaling with workflows and volume."
      canonical="https://lumient.ai/pricing"
    />

    <main className="flex-1 w-full">

      {/* ── Hero ── */}
      <section className="relative px-6 lg:px-16 pt-36 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.18) 1.2px, transparent 1.2px)",
            backgroundSize: "22px 22px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(200,144,10,0.12) 0%, transparent 70%)" }}
        />

        <motion.div
          className="relative max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-xs font-medium text-[oklch(var(--primary-bright-readable))] tracking-[0.2em] uppercase mb-6">
            Pricing
          </p>
          <h1 className="font-serif font-semibold text-5xl lg:text-6xl leading-[1.08] tracking-tight text-white mb-6">
            Start with a pilot.<br />Scale when it works.
          </h1>
          <p className="text-lg text-surface-inverse-muted leading-relaxed max-w-xl mx-auto font-light">
            We don't ask you to buy before you've seen results.
            Start with one workflow, prove the value, then move to a production contract.
          </p>
        </motion.div>
      </section>

      {/* ── Cards ── */}
      <section className="px-6 lg:px-16 pb-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {plans.map((plan, i) => (
            <PlanCard key={plan.name} plan={plan} index={i} />
          ))}
        </div>

        <motion.p
          className="text-center text-xs text-surface-inverse-muted mt-10 max-w-lg mx-auto leading-relaxed font-light"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          All plans are annual contracts. Pricing scales with the number of active workflows
          and monthly document volume. Talk to us if your needs don't fit neatly into a tier —
          we work with every team individually.
        </motion.p>
      </section>

      {/* ── FAQ strip ── */}
      <section className="border-t border-white/8 px-6 lg:px-16 py-16 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
          {[
            {
              q: "What counts as a workflow?",
              a: "A workflow is a defined extraction and tracking process — for example, monitoring renewal dates across all vendor contracts, or extracting payment terms from invoices. Each workflow runs continuously on new and updated documents.",
            },
            {
              q: "What happens after the pilot?",
              a: "At the end of the pilot period you'll have a working workflow and measurable results. We'll work with you on a Team contract scoped to your actual usage.",
            },
            {
              q: "How does document volume work?",
              a: "Volume is measured by the number of documents processed each month — new ingestion plus re-processing when documents are updated. We scope this with you at the start so there are no surprises.",
            },
          ].map(({ q, a }, i) => (
            <motion.div
              key={q}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
            >
              <h3 className="font-serif font-semibold text-sm text-white mb-3 tracking-tight">{q}</h3>
              <p className="text-sm text-surface-inverse-muted leading-relaxed font-light">{a}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="border-t border-white/8 px-6 lg:px-16 py-16">
        <motion.div
          className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div>
            <h2 className="font-serif font-semibold text-2xl text-white tracking-tight mb-2">
              Not sure which tier fits?
            </h2>
            <p className="text-sm text-surface-inverse-muted font-light">
              Tell us what you're tracking and we'll figure it out together.
            </p>
          </div>
          <a
            href="/contact"
            className="glow-button flex-shrink-0 px-7 py-3 text-sm font-semibold rounded-full whitespace-nowrap"
          >
            Book a Demo
          </a>
        </motion.div>
      </section>

    </main>
  </div>
);

export default Pricing;
